// V tomto modulu není třeba používat TypeDoc komentáře s @ignore,
// protože je exlcudován z generování dokumentace v tsconfig.json.

import {
    BaseMapEditor,
    BaseMapEditorProps,
    BaseMapViewer,
    BaseMapViewerProps,
    MapEditor,
    MapEditorProps,
    MapViewer,
    MapViewerProps,
} from './components';
import * as React from 'react';
import {
    DateFilterItem,
    EditorEventType,
    EditorLayersMode,
    EditorTool,
    EditorToolType,
    FeatureTooltips,
    FeatureZoom,
    FloatPosition,
    KeyboardShortcut,
    SymbolicDate,
    ViewerEventType,
    ViewerTool,
    ViewerToolType,
} from './enums';
import {Feature, GeoJsonProperties} from 'geojson';
import {MapReducers} from './reducers';
import {ConfigActions, EditorActions, FeatureActions, MapActions as NewMapActions} from './actions';
import {buildMapioUrl} from './utils';

const doneWarnings = {};

function warnOnce(key: string, message: string) {
    if (!doneWarnings[key]) {
        doneWarnings[key] = true;
        console.warn('[Deprecation] ' + message);
    }
}

export class BaseMap extends React.PureComponent<BaseMapViewerProps> {
    static ToolType = ViewerToolType;
    static ToolPosition = FloatPosition;
    static Tool = ViewerTool;
    static EventType = ViewerEventType;
    static SymbolicDate = SymbolicDate;
    static DateFilterItem = DateFilterItem;

    static propTypes = BaseMapViewer.propTypes;
    static defaultProps = BaseMapViewer.defaultProps;

    componentDidMount(): void {
        warnOnce('BaseMap', 'BaseMap is deprecated. Use BaseMapViewer from "@mc/react-mapio".');
    }

    render(): JSX.Element {
        return <BaseMapViewer {...this.props} />;
    }
}

export class Map extends React.PureComponent<MapViewerProps> {
    static ToolType = ViewerToolType;
    static ToolPosition = FloatPosition;
    static Tool = ViewerTool;
    static EventType = ViewerEventType;
    static SymbolicDate = SymbolicDate;
    static DateFilterItem = DateFilterItem;

    static propTypes = MapViewer.propTypes;
    static defaultProps = MapViewer.defaultProps;

    componentDidMount(): void {
        warnOnce('Map', 'Map is deprecated. Use MapViewer from "@mc/react-mapio".');
    }

    render(): JSX.Element {
        return <MapViewer {...this.props} />;
    }
}

interface FeatureEditorLegacyProps {
    initialFeaturesZoom: FeatureZoom;
}

export class BaseFeatureEditor extends React.PureComponent<BaseMapEditorProps & FeatureEditorLegacyProps> {
    static LayersMode = EditorLayersMode;
    static ToolType = EditorToolType;
    static Tool = EditorTool;
    static KeyboardShortcut = KeyboardShortcut;
    static FeatureZoom = FeatureZoom;
    static FeatureTooltips = FeatureTooltips;
    static EventType = EditorEventType;
    static SymbolicDate = SymbolicDate;
    static DateFilterItem = DateFilterItem;

    static propTypes = BaseMapEditor.propTypes;
    static defaultProps = BaseMapEditor.defaultProps;

    componentDidMount(): void {
        warnOnce('BaseFeatureEditor', 'BaseFeatureEditor is deprecated. Use BaseMapEditor from "@mc/react-mapio".');
    }

    render(): JSX.Element {
        return <BaseMapEditor {...transformFeatureEditorLegacyProps(this.props)} />;
    }
}

export class FeatureEditor extends React.PureComponent<MapEditorProps & FeatureEditorLegacyProps> {
    static LayersMode = EditorLayersMode;
    static ToolType = EditorToolType;
    static Tool = EditorTool;
    static KeyboardShortcut = KeyboardShortcut;
    static FeatureZoom = FeatureZoom;
    static FeatureTooltips = FeatureTooltips;
    static EventType = EditorEventType;
    static SymbolicDate = SymbolicDate;
    static DateFilterItem = DateFilterItem;

    static propTypes = MapEditor.propTypes;
    static defaultProps = MapEditor.defaultProps;

    componentDidMount(): void {
        warnOnce('FeatureEditor', 'FeatureEditor is deprecated. Use MapEditor from "@mc/react-mapio".');
    }

    render(): JSX.Element {
        return <MapEditor {...transformFeatureEditorLegacyProps(this.props)} />;
    }
}

function transformFeatureEditorLegacyProps<T extends BaseMapEditorProps>(props: T & FeatureEditorLegacyProps): T {
    const {initialFeaturesZoom, ...otherProps} = props;
    if (initialFeaturesZoom !== undefined) {
        if (!otherProps.initialFeaturesOptions) {
            otherProps.initialFeaturesOptions = {};
        }
        otherProps.initialFeaturesOptions.zoom = initialFeaturesZoom;
    }
    return (otherProps as any) as T;
}

export const BaseMapActions = {
    ...NewMapActions,
};

export const BaseFeatureEditorActions = {
    ...NewMapActions,
    ...FeatureActions,
    ...EditorActions,
};

export const MapActions = {
    ...BaseMapActions,
    ...ConfigActions,
};

export const FeatureEditorActions = {
    ...BaseFeatureEditorActions,
    ...ConfigActions,
};

export const BaseMapReducer = BaseAbstractMapReducer;

export const BaseFeatureEditorReducer = BaseAbstractMapReducer;

export const MapReducer = AbstractMapReducer;

export const FeatureEditorReducer = AbstractMapReducer;

function BaseAbstractMapReducer(state, action) {
    warnOnce(
        'BaseAbstractMapReducer',
        'BaseMapReducer and BaseFeatureEditorReducer are deprecated. Use MapReducers.base from "@mc/react-mapio".',
    );

    if (state) {
        return state.reducer(state, action);
    }

    return MapReducers.base();
}

function AbstractMapReducer(state, action, customInitialState) {
    warnOnce(
        'AbstractMapReducer',
        'MapReducer and FeatureEditorReducer are deprecated. Use MapReducers.full from "@mc/react-mapio".',
    );

    if (state) {
        return state.reducer(state, action);
    }

    return MapReducers.full(customInitialState);
}

AbstractMapReducer.fromValues = function(values) {
    warnOnce(
        'AbstractMapReducer.fromValues',
        'MapReducer.fromValues and FeatureEditorReducer.fromValues are deprecated.' +
            ' Use MapReducers.full from "@mc/react-mapio".',
    );

    return MapReducers.full(values);
};

AbstractMapReducer.fromLocalStorage = function(localStorageKey, fixedConfig, defaultConfig) {
    warnOnce(
        'AbstractMapReducer.fromLocalStorage',
        'MapReducer.fromLocalStorage and FeatureEditorReducer.fromLocalStorage are deprecated.' +
            ' Use MapReducers.fromLocalStorage from "@mc/react-mapio".',
    );

    return MapReducers.fromLocalStorage(localStorageKey, fixedConfig, defaultConfig);
};

AbstractMapReducer.persistInLocalStorage = function(store, areas) {
    warnOnce(
        'AbstractMapReducer.persistInLocalStorage',
        'MapReducer.persistInLocalStorage and FeatureEditorReducer.persistInLocalStorage are deprecated.' +
            ' Use MapReducers.fromLocalStorage from "@mc/react-mapio".',
    );

    return MapReducers.persistInLocalStorage(store, areas);
};

export const base = {
    Map: BaseMap,
    MapActions: BaseMapActions,
    MapReducer: BaseMapReducer,
    FeatureEditor: BaseFeatureEditor,
    FeatureEditorActions: BaseFeatureEditorActions,
    FeatureEditorReducer: BaseFeatureEditorReducer,
};

export const stateful = {
    Map,
    MapActions,
    MapReducer,
    FeatureEditor,
    FeatureEditorActions,
    FeatureEditorReducer,
};

export function addFeatureProperties(feature: Feature, properties: GeoJsonProperties): Feature {
    if (feature.properties) {
        return {...feature, properties: {...feature.properties, ...properties}};
    }
    return {...feature, properties};
}

export function removeFeatureProperties(feature: Feature, names: string[]): Feature {
    const newFeature = {
        ...feature,
        properties: feature.properties ? {...feature.properties} : null,
    };

    if (newFeature.properties) {
        for (const name of names) {
            delete newFeature.properties[name];
        }

        if (!Object.keys(newFeature.properties).length) {
            newFeature.properties = null;
        }
    }

    return newFeature;
}

export function getStateProps(state: {[key: string]: any}): {[key: string]: any} {
    const stateProps = [
        'topic',
        'backgroundLayer',
        'foregroundLayer',
        'layers',
        'catalogNodes',
        'location',
        'zoom',
        'filter',
    ];
    const result = {};
    if (state.config) {
        for (const name of stateProps) {
            const value = state.config[name];
            if (value !== undefined) {
                result[name] = value;
            }
        }
    }
    return result;
}

export {buildMapioUrl};
